import {
  useMutation,
  type UseMutationOptions,
  useQueryClient,
} from '@tanstack/react-query';

import { queryKeysInvoiceReprocessing } from '../queryKeys';

import { addInvoiceReprocessingComment } from './addInvoiceReprocessingComment';
import { type AddInvoiceReprocessingCommentParams } from './types';

/**
 * A custom hook to add a comment to the reprocessing invoice with a given process ID.
 * Uses the `useMutation` hook from `@tanstack/react-query` internally.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/Invoice/operation/set_process_comment_invoice_process__process_id__comment_put
 */
export const useMutationAddInvoiceReprocessingComment = (
  options?: UseMutationOptions<
    unknown,
    unknown,
    AddInvoiceReprocessingCommentParams
  >,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      comment,
      processId,
    }: AddInvoiceReprocessingCommentParams) =>
      addInvoiceReprocessingComment(processId, comment),
    onError(_error) {
      return; // prevent error from bubbling up further unless overridden via mutation options
    },
    onSettled(_data, _error, { processId }) {
      void queryClient.invalidateQueries({
        queryKey: queryKeysInvoiceReprocessing.getAll({}),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeysInvoiceReprocessing.get(processId),
      });
    },
    ...options,
  });
};
